// AdminWelcomeSettings — iter179
// Tenant-level configuration for the post-login Welcome Overlay.
//
// Controls:
//   • overlay_mode: 'every_login' | 'daily' | 'disabled'
//   • welcome_heading (override greeting)
//   • welcome_subtext (override secondary line)
//   • welcome_quote_override (replace today's quote with custom one)
//   • landing_module (per-tenant override — list of landing-eligible features)
//
// Mirrors backend PUT /api/tenant/welcome-settings.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Sparkles, Save, RotateCcw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Landing-eligible feature keys — mirrors backend FEATURE_TO_ADMIN_PATH.
const LANDING_OPTIONS = [
  { key: 'dashboard',              label: 'Dashboard' },
  { key: 'financial_dashboard',    label: '5-Minute Dashboard' },
  { key: 'pnl_live',               label: 'P&L Live' },
  { key: 'cost_insights',          label: 'Cost Insights' },
  { key: 'crm',                    label: 'Customers (CRM)' },
  { key: 'customer_feedback',      label: 'Customer Experience' },
  { key: 'custom_feedback_forms',  label: 'Customer Pulse Index' },
  { key: 'reservations_module',    label: 'Reservations' },
  { key: 'checklist',              label: 'Daily Checklist' },
  { key: 'maintenance',            label: 'Maintenance' },
  { key: 'inventory_module',       label: 'Inventory' },
  { key: 'recipe_module',          label: 'Recipe Studio' },
  { key: 'production_planner',     label: 'Production Planner' },
  { key: 'petty_cash',             label: 'Petty Cash' },
  { key: 'communication',          label: 'Communication' },
  { key: 'marketing',              label: 'Marketing' },
  { key: 'user_management',        label: 'Managers & Users' },
  { key: 'outlet_management',      label: 'Outlets' },
];

export default function AdminWelcomeSettings() {
  const { getHeaders, tenantFeatures } = useAuth();
  const [state, setState] = useState({
    welcome_heading: '',
    welcome_subtext: '',
    welcome_quote_override: '',
    welcome_overlay_mode: 'daily',
    landing_module: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/tenant/welcome-settings`, { headers: getHeaders() });
      setState({
        welcome_heading: r.data.welcome_heading || '',
        welcome_subtext: r.data.welcome_subtext || '',
        welcome_quote_override: r.data.welcome_quote_override || '',
        welcome_overlay_mode: r.data.welcome_overlay_mode || 'daily',
        landing_module: r.data.landing_module || '',
      });
    } catch (e) {
      toast.error('Failed to load welcome settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/tenant/welcome-settings`, state, { headers: getHeaders() });
      toast.success('Welcome experience updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const previewOverlay = () => {
    // Clear daily dedupe key for current tenant so overlay shows immediately.
    Object.keys(localStorage)
      .filter((k) => k.startsWith('welcome_seen_'))
      .forEach((k) => localStorage.removeItem(k));
    toast.info('Sign out and sign back in to preview your changes.');
  };

  if (loading) return null;

  return (
    <Card className="border-[#E2E8DF] shadow-sm mt-6" data-testid="welcome-settings-card">
      <CardHeader className="py-3 px-4 border-b border-[#E2E8DF] bg-[#F9F9F8]">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[color:var(--color-primary)]" />
            Welcome Experience
          </CardTitle>
          <span className="text-[10px] uppercase tracking-wider text-[#9CA3A0]">Post-login overlay</span>
        </div>
      </CardHeader>
      <CardContent className="px-4 py-5 space-y-5">
        {/* Mode */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label className="text-[12px] font-semibold text-[#1A2421]">Overlay frequency</Label>
            <Select
              value={state.welcome_overlay_mode}
              onValueChange={(v) => setState({ ...state, welcome_overlay_mode: v })}
            >
              <SelectTrigger className="mt-1.5 h-9 text-[13px]" data-testid="welcome-overlay-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Once per day (recommended)</SelectItem>
                <SelectItem value="every_login">Every login</SelectItem>
                <SelectItem value="disabled">Disabled</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-[#5C6B64] mt-1.5 leading-relaxed">
              How often the welcome overlay appears for your team. Operational users
              who sign in multiple times a day usually prefer "Once per day".
            </p>
          </div>
          <div>
            <Label className="text-[12px] font-semibold text-[#1A2421]">Landing page override</Label>
            <Select
              value={state.landing_module || '__chain__'}
              onValueChange={(v) => setState({ ...state, landing_module: v === '__chain__' ? '' : v })}
            >
              <SelectTrigger className="mt-1.5 h-9 text-[13px]" data-testid="welcome-landing-module">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__chain__">Automatic (follow operational priority)</SelectItem>
                {LANDING_OPTIONS
                  .filter((o) => tenantFeatures?.[o.key] !== false)
                  .map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-[#5C6B64] mt-1.5 leading-relaxed">
              Send everyone in this tenant straight to this module after login.
              Only enabled features are listed. Falls back automatically if
              this feature is later disabled.
            </p>
          </div>
        </div>

        {/* Heading + Subtext */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label className="text-[12px] font-semibold text-[#1A2421]">Custom heading</Label>
            <Input
              value={state.welcome_heading}
              onChange={(e) => setState({ ...state, welcome_heading: e.target.value })}
              placeholder="Leave blank for auto time-of-day greeting"
              className="mt-1.5 h-9 text-[13px]"
              maxLength={140}
              data-testid="welcome-heading-input"
            />
          </div>
          <div>
            <Label className="text-[12px] font-semibold text-[#1A2421]">Custom subtext</Label>
            <Input
              value={state.welcome_subtext}
              onChange={(e) => setState({ ...state, welcome_subtext: e.target.value })}
              placeholder="One-line operational message"
              className="mt-1.5 h-9 text-[13px]"
              maxLength={280}
              data-testid="welcome-subtext-input"
            />
          </div>
        </div>

        {/* Quote override */}
        <div>
          <Label className="text-[12px] font-semibold text-[#1A2421]">Quote override</Label>
          <Textarea
            value={state.welcome_quote_override}
            onChange={(e) => setState({ ...state, welcome_quote_override: e.target.value })}
            placeholder="Leave blank to use today's hospitality quote. Type a custom message to override."
            rows={2}
            maxLength={400}
            className="mt-1.5 text-[13px]"
            data-testid="welcome-quote-input"
          />
          <p className="text-[11px] text-[#5C6B64] mt-1.5 leading-relaxed">
            Default behavior: rotates daily through 365 curated hospitality, leadership
            and team-work quotes. Same quote for every user on the same day.
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-1">
          <Button
            variant="outline"
            className="h-9 text-[13px]"
            onClick={previewOverlay}
            data-testid="welcome-preview-btn"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
            Reset daily seen
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[color:var(--color-primary)] hover:bg-[#0B2A22] h-9 text-[13px]"
            data-testid="welcome-save-btn"
          >
            <Save className="w-3.5 h-3.5 mr-1.5" />
            {saving ? 'Saving…' : 'Save welcome settings'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
